import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import { PreferencesDataValues } from '../../../models/preferencesDataValues';
import { Measure } from '../../../utils/parcel-measurements.ts/parcel-measurements-utils';
import { IDropdownItem } from '../../../common/select/select.component';
import { CountriesResponse } from '../../../models/countriesModel';
import useNavigateNewLocale from '../../../hooks/use-navigate-to-new-locale.hook';
import { getLocalizationsMap } from '../../../constants/languages';
import { Language } from '../../../models/language';

export const useRegionSettings = () => {
  const { i18n } = useTranslation();

  const {
    userStore: { user, updatePreferences },
    localizationsStore: {
      availableCountries,
      selectedLocalization,
      selectedCountry,
      getCountryLocalizedTitle,
    },
    commonStore: { toastSuccess },
  } = useStore();

  const [country, setCountry] = useState<CountriesResponse>();
  const [language, setLanguage] = useState<string>();
  const [currency, setCurrency] = useState<string>();
  const [measures, setMeasures] = useState<Measure>();
  const [isSaving, setIsSaving] = useState(false);
  const [currentAvailableLang, setCurrentAvailableLang] = useState<Language[]>(
    []
  );
  const [loadedLanguage, setLoadedLanguage] = useState('en');

  const isGuest = !user;

  const updateAvailableLanguages = (c: CountriesResponse) => {
    const localizationMap = getLocalizationsMap(c.country_code);
    const availableLang = c.allowed_languages
      .map((lang) => localizationMap[lang])
      .filter(Boolean);

    setCurrentAvailableLang(availableLang);
  };

  useEffect(() => {
    if (!selectedCountry) return;

    setCountry(selectedCountry);

    updateAvailableLanguages(selectedCountry);

    if (user) {
      // Logged user → use preferences
      setLanguage(user.lang);
      setCurrency(user.currency);
      setMeasures(user.measures);
    } else {
      // Guest → use country defaults
      setLanguage(selectedLocalization?.code);
      setCurrency(selectedCountry.default_currency);
      setMeasures(selectedCountry.default_measures);
    }
  }, [user, selectedCountry, selectedLocalization]);

  useEffect(() => {
    const nextLanguage =
      language ?? selectedLocalization?.code ?? i18n.resolvedLanguage ?? 'en';
    let isMounted = true;

    const loadSelectedLanguage = async () => {
      await i18n.loadLanguages(nextLanguage);

      if (isMounted) {
        setLoadedLanguage(nextLanguage);
      }
    };

    loadSelectedLanguage().catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, [i18n, language, selectedLocalization]);

  const tBySelectedLanguage = useMemo(
    () => i18n.getFixedT(loadedLanguage),
    [i18n, loadedLanguage]
  );

  const isDirty = useMemo(() => {
    if (!country || !language || !currency || measures === undefined)
      return false;

    const isCountryChanged =
      country.country_code !== selectedCountry?.country_code;

    if (user) {
      return (
        isCountryChanged ||
        language !== user.lang ||
        currency !== user.currency ||
        measures !== user.measures
      );
    }

    return (
      isCountryChanged ||
      language !== selectedLocalization?.code ||
      currency !== selectedCountry?.default_currency ||
      measures !== selectedCountry?.default_measures
    );
  }, [
    user,
    country,
    language,
    currency,
    measures,
    selectedCountry,
    selectedLocalization,
  ]);

  const countryItems = useMemo(
    () =>
      availableCountries?.map((c) => ({
        value: c,
        label: getCountryLocalizedTitle(c, true, language),
      })),
    [availableCountries, getCountryLocalizedTitle, language]
  );

  const languageItems: IDropdownItem<string>[] = useMemo(
    () =>
      currentAvailableLang.map((l) => ({
        label: `${l.code.toUpperCase()} - ${l.languageLabel}`,
        value: l.code,
      })),
    [currentAvailableLang]
  );

  const currencyItems: IDropdownItem<string>[] = useMemo(() => {
    const unique = Array.from(
      new Set((availableCountries ?? []).map((c) => c.default_currency))
    );

    return unique.map((cur) => ({
      label: cur,
      value: cur,
    }));
  }, [availableCountries]);

  const measureItems: IDropdownItem<string>[] = useMemo(
    () => [
      { label: tBySelectedLanguage('kg_/_cm'), value: String(Measure.KG_CM) },
      { label: tBySelectedLanguage('lb_/_in'), value: String(Measure.LB_IN) },
    ],
    [tBySelectedLanguage]
  );

  const navigateToNewLocale = useNavigateNewLocale();

  const handleCountryChange = (item: IDropdownItem<CountriesResponse>) => {
    updateAvailableLanguages(item.value);

    const newCountry = item.value;

    setCountry(newCountry);

    setCurrency(newCountry.default_currency);
    setMeasures(newCountry.default_measures);

    const currentLang = language ?? selectedLocalization?.code;

    const isCurrentLangSupported =
      !!currentLang && newCountry.allowed_languages?.includes(currentLang);

    if (!isCurrentLangSupported) {
      setLanguage(newCountry.default_language);
    }
  };

  const handleSave = async (): Promise<boolean> => {
    if (!isDirty) return false;

    if (!country || !language || !currency || measures === undefined)
      return false;

    const payload: PreferencesDataValues = {
      lang: language,
      currency,
      measures,
    };

    setIsSaving(true);
    try {
      if (!user) {
        navigateToNewLocale({
          countryCode: country.alias.toLowerCase(),
          langCode: language,
          withPageReload: true,
        });
        return true;
      }

      const success = await updatePreferences(payload);
      if (!success) return false;

      toastSuccess(tBySelectedLanguage('data_updated_successfully'));

      navigateToNewLocale({
        countryCode: country.alias.toLowerCase(),
        langCode: language,
        withPageReload: true,
      });

      return true;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    t: tBySelectedLanguage,
    loadedLanguage,
    country,
    language,
    currency,
    measures,
    setLanguage,
    setCurrency,
    setMeasures,
    handleCountryChange,
    handleSave,
    isSaving,
    isDirty,
    countryItems,
    languageItems,
    currencyItems,
    measureItems,
    isGuest,
    getCountryLocalizedTitle,
  };
};
